<template>
  <q-layout view="hHh lpR lFf" class="layout">
    <div class="layout-content">
      <BaseLayout v-if="isAuth && !authInProcess" :isLoading="isLoading" />
      <router-view v-else>
      </router-view>
    </div>

    <q-inner-loading v-if="isLoading" :showing="isLoading">
      <AppLoader />
    </q-inner-loading>

    <footer class="ecos-footer">

      <div class="ecos-footer__container">

        <div class="ecos-footer__col ecos-footer__brand">
          <div class="ecos-footer__logo-plate">
            <img
              src="@/assets/ecosafety_mc_logo_2017.png"
              alt="Эко-безопасность"
              class="ecos-footer__logo"
            >
          </div>

          <div>
            <div class="ecos-footer__title">
              Группа компаний Эко-безопасность
            </div>

            <div class="ecos-footer__copy">
              © 2011–{{ new Date().getFullYear() }}
            </div>
          </div>
        </div>

        <div class="ecos-footer__divider" />

        <div class="ecos-footer__col">
          <div class="ecos-footer__label">Контакты</div>

          <q-btn
            no-caps
            flat
            dense
            class="ecos-footer__link"
            icon="call"
            label="+7 (812) 325-03-05"
            href="tel:+78123250305"
          />

          <q-btn
            no-caps
            flat
            dense
            class="ecos-footer__link"
            icon="email"
            label="info@ecosafety.ru"
            href="mailto:info@ecosafety.ru"
          />
        </div>

        <div class="ecos-footer__divider" />

        <div class="ecos-footer__col ecos-footer__col--address">
          <div class="ecos-footer__label">Адрес</div>
          <div class="ecos-footer__address">
            191119, Санкт-Петербург,<br>
            ул. Достоевского, 40-44
          </div>
        </div>

      </div>

    </footer>

    <q-dialog
      v-model='needChange'
      persistent
      transition-show="scale"
      transition-hide="scale"
      class='first-acquaintance'
    >
      <q-card class="gu-confirm-card">
        <div class="gu-confirm-icon">
          <q-icon name="lock" size="26px" />
        </div>

        <div class="gu-confirm-title">Измените пароль</div>

        <div class="gu-confirm-text">
          Это ваш первый вход в личный кабинет. Задайте новый пароль, чтобы продолжить.
        </div>

        <q-form @submit.prevent="tryChangePass">
          <div class="gu-confirm-fields">
            <q-input
              dense
              outlined
              :type="isPwd ? 'password' : 'text'"
              v-model="data.newPpassword"
              label="Новый пароль"
              lazy-rules
              bg-color="white"
              :rules="[
                val => val !== null && val !== '' || 'Обязательное поле',
              ]"
            >
              <template v-slot:append>
                <q-icon
                  :name="isPwd ? 'visibility_off' : 'visibility'"
                  class="cursor-pointer"
                  @click="isPwd = !isPwd"
                />
              </template>
            </q-input>

            <q-input
              dense
              outlined
              :type="isPwdRepeat ? 'password' : 'text'"
              v-model="data.newPpasswordRepeat"
              label="Еще раз"
              lazy-rules
              bg-color="white"
              :rules="[
                val => val !== null && val !== '' || 'Обязательное поле',
              ]"
            >
              <template v-slot:append>
                <q-icon
                  :name="isPwdRepeat ? 'visibility_off' : 'visibility'"
                  class="cursor-pointer"
                  @click="isPwdRepeat = !isPwdRepeat"
                />
              </template>
            </q-input>
          </div>

          <div class="gu-confirm-actions">
            <q-btn
              unelevated
              no-caps
              :ripple="false"
              icon="logout"
              class="gu-btn gu-btn-secondary"
              label="Выйти"
              @click.prevent="logout"
            />
            <q-btn
              unelevated
              no-caps
              :ripple="false"
              icon="save"
              class="gu-btn gu-btn-primary"
              label="Сохранить"
              type="submit"
            />
          </div>
        </q-form>
      </q-card>
    </q-dialog>

    <q-dialog
      v-model="modal"
      :maximized="maximized"
      :full-height="maximized"
      :full-width="maximized"
      :class="{
        'prepare-report': reportMode,
      }"
    >
      <Suspense>
        <ModalContainer @close="closeModal"> </ModalContainer>
      </Suspense>
    </q-dialog>
  </q-layout>
</template>

<script setup lang="ts">
import {
  watchEffect,
  computed,
  reactive,
  ref,
  watch,
} from 'vue';
import ModalContainer from '~components/modals/ModalContainer.vue';
import { useSocket } from '@/utils/socket';
import { useRoute } from 'vue-router';
import { useMainStore } from '@/store';
import { useAuthStore } from '@/store/auth/authModule';
import {
  storeToRefs,
  getActivePinia,
  Pinia,
  Store,
} from 'pinia';
import AppLoader from '~components/AppLoader.vue';
import BaseLayout from '@/layouts/BaseLayout.vue';
// import AuthLayout from '@/layouts/AuthLayout.vue';
import { height, tabsHeaderHeight } from './constants';

interface ExtendedPinia extends Pinia {
  _s: Map<string, Store>;
}

const route = useRoute();

const $ResetPinia = (): void => {
  const pinia = getActivePinia() as ExtendedPinia;

  if (!pinia) {
    throw new Error('There is no stores');
  }

  pinia._s.forEach((store) => {
    store.$reset();
  });
};

const authStore = useAuthStore();

const isLoading = ref(false);

const { isAuth, needChange } = storeToRefs(authStore);

const authInProcess = ref(false);
const isPwd = ref(true);
const isPwdRepeat = ref(true);
const data = reactive({
  newPpassword: '',
  newPpasswordRepeat: '',
});

const {
  modal,
  maximized,
  reportMode,
} = storeToRefs(useMainStore());

const { emit, on, connected } = useSocket();

// onMounted(() => {
//   const offRefresh = on('refresh', (data) => {
//     console.log('Получено обновление:', data);
//     // обнови состояние, store, dispatch event и т.д.
// });

// onUnmounted(() => {
//   offRefresh();
// });
// onBeforeMount(async () => {
//   isLoading.value = true;
//   isLoading.value = false;
// });

const closeModal = () => {
  useMainStore().closeModal();
};

watch(
  () => authStore.processing,
  () => {
    isLoading.value = authStore.processing;
  },
);

const tryChangePass = async () => {
  if (data.newPpassword === data.newPpasswordRepeat) {
    let hash = '';
    if (authStore.hash) {
      hash = authStore.hash;
    }
    const result = await authStore.changePassword(data.newPpassword, hash);
    if (result) {
      needChange.value = false;
      authStore.hash = '';
      authStore.autenticate();
      useMainStore().showMessage(authStore.user.err_text);
    }
  } else {
    useMainStore().showMessage('Пароли не совпадают');
  }
};

const logout = () => {
  $ResetPinia();
  useAuthStore().logout();
};

const gradientClass = computed(() => {
  console.log('route.name', route.name);
  switch (route.name) {
    case undefined:
      return 'bg-login-layout';
    case 'Login':
      return 'bg-login-layout';
    default:
      return 'bg-main-layout';
  }
});

watchEffect(() => {
  document.body.className = gradientClass.value;
});

const wrapperStyleHeight = `calc(100vh - ${height}px)`;
const tabsWrapperStyleHeight = `calc(100vh - ${height + tabsHeaderHeight}px)`;
</script>

<style lang="scss">
noscript {
  display: none;
}
.bg-main-layout {
  background: #0b2d6b;
}
.bg-login-layout {
  background: #FFF;
}
.prepare-report.fullscreen,
.prepare-report .fixed,
.prepare-report .fixed-full {
  position: absolute !important;
}

.small-text * {
  font-size: 0.6rem;
}

.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.layout-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.q-page-container {
  flex: 1;
}
.auth-banner {
  flex-direction: column;
}
.first-acquaintance .q-field--outlined .q-field__control {
  border-radius: 12px;
  background: #fafbff;
  border: 1px solid #e6e8ef;
}
.first-acquaintance .q-field--focused .q-field__control {
  border-color: #2563eb;
}

.gu-confirm-card {
  width: 400px;
  max-width: calc(100vw - 32px);
  padding: 32px 28px 28px;
  border-radius: 26px !important;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(13, 76, 211, 0.16);
}

.gu-confirm-icon {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px !important;
  background: #e4ecfd;
  color: #0d4cd3;
  margin-bottom: 16px;
}

.gu-confirm-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.gu-confirm-text {
  font-size: 15px;
  line-height: 1.5;
  color: #4b5563;
  margin-bottom: 24px;
}

.gu-confirm-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.gu-confirm-actions {
  display: flex;
  gap: 12px;
}

.gu-confirm-actions .gu-btn {
  flex: 1;
}

.gu-btn {
  height: 48px;
  padding: 0 22px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;
  transition: background-color .15s ease, border-color .15s ease;
}

.gu-btn-primary {
  background: #0d4cd3;
  color: #fff;
}

.gu-btn-primary:hover {
  background: #1d5deb;
  color: #fff;
}

.gu-btn-primary:active,
.gu-btn-primary:focus {
  background: #2a63ad;
}

.gu-btn-secondary {
  background: #fff;
  color: #0d4cd3;
  border: 2px solid #0d4cd3;
}

.gu-btn-secondary:hover {
  background: #e4ecfd;
}

.gu-btn-secondary:active,
.gu-btn-secondary:focus {
  background: #d3e0fb;
}

@media (max-width: 480px) {
  .gu-confirm-actions {
    flex-direction: column-reverse;
  }
}

.container {
  flex: 1;
  min-height: 0;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
}
.button-group {
  justify-content: space-between;
}
.fab {
  position: absolute !important;
  z-index: 10000 !important;
  &_centered {
    top: calc(50% - 28px);
    left: 50%;
    transform: translateX(-28px);
  }
  &_bottom-right {
    bottom: 28px;
    right: 28px;
  }
}
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}
.q-dialog__inner--minimized {
  padding: 5px !important;
}
.q-field__bottom {
  padding: 1px 12px 0px !important;
}
.ecos-footer {
  flex-shrink: 0;
  padding: 40px 48px;
  background: #0d2b5e;
  color: rgba(255, 255, 255, 0.72);
}

.ecos-footer__container {
  display: flex;
  align-items: flex-start;
  gap: 40px;
  max-width: 1150px;
  margin: 0 auto;
}

.ecos-footer__col {
  flex: 1;
  min-width: 0;
}

.ecos-footer__col--address {
  text-align: right;
}

.ecos-footer__divider {
  align-self: stretch;
  width: 1px;
  background: rgba(255, 255, 255, 0.14);
}

.ecos-footer__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
}

.ecos-footer__logo-plate {
  flex-shrink: 0;
  width: 185px;
  height: 92px;
  padding: 14px;
  border-radius: 18px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ecos-footer__logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ecos-footer__title {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.01em;
}

.ecos-footer__copy {
  margin-top: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

.ecos-footer__label {
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.4);
}

.ecos-footer__link {
  display: flex;
  width: fit-content;
  margin-bottom: 6px;
  color: rgba(255, 255, 255, 0.85) !important;
  transition: color .15s ease;
}

.ecos-footer__col--address .ecos-footer__link {
  margin-left: auto;
}

.ecos-footer__link:hover {
  color: #fff !important;
}

.ecos-footer__address {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
}

.app-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

@media (max-width: 900px) {

  .ecos-footer {
    padding: 28px 20px;
  }

  .ecos-footer__container {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .ecos-footer__divider {
    display: none;
  }

  .ecos-footer__col,
  .ecos-footer__col--address {
    text-align: center;
    width: 100%;
  }

  .ecos-footer__brand {
    align-items: center;
    text-align: center;
  }

  .ecos-footer__link,
  .ecos-footer__col--address .ecos-footer__link {
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
